import { FC, useCallback, useEffect, useMemo } from 'react';
import { Box, CircularProgress, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import { KeyboardBackspace } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { COLORS } from 'services/constants/colors';
import { PATHS } from 'routes/constants';
import { getSavedUrl } from 'redux/reducers/auth/selectors';
import {
	clearCreditNoteDetails,
	getCreditNoteRequest,
} from 'redux/reducers/invoiceManagement/reducer';
import {
	selectCreditNoteDetails,
	selectCreditNoteDetailsLoading,
} from 'redux/reducers/invoiceManagement/selectors';
import { TCreditNote } from 'redux/reducers/invoiceManagement/types';
import { EasParamsBox } from 'components/EMManagement/EMManagementId/EasParamsBox';
import { EMManagementIdHeader } from 'components/EMManagement/EMManagementId/EMManagementIdHeader';
import { EasTableWithoutFilter } from 'ui/EasTableWithoutFilter';
import { creditNoteBoxStructure } from './boxStructure';
import { invoicesTableStructure, TCreditNoteInvoiceRow } from './invoicesTableStructure';

export const CreditNoteDetails: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { creditNoteId: creditNoteIdParam } = useParams<{ creditNoteId: string }>();
	const creditNoteId = Number(creditNoteIdParam);

	const savedUrl = useSelector(getSavedUrl);
	const details = useSelector(selectCreditNoteDetails);
	const loading = useSelector(selectCreditNoteDetailsLoading);

	useEffect(() => {
		if (Number.isFinite(creditNoteId)) dispatch(getCreditNoteRequest({ id: creditNoteId }));
		return () => {
			dispatch(clearCreditNoteDetails());
		};
	}, [dispatch, creditNoteId]);

	/** A previously opened credit note may still be in the store until this one loads. */
	const creditNote = details?.id === creditNoteId ? details : null;

	const invoices = useMemo(() => {
		const data: TCreditNoteInvoiceRow[] = (creditNote?.invoices ?? []).map((invoice) => ({
			...invoice,
			id: invoice.invoiceId,
		}));
		return { data, pageNumber: 1, pageSize: data.length, totalCount: data.length };
	}, [creditNote]);

	const handleAction = useCallback(() => {}, []);

	const handleBack = () => {
		if (savedUrl && savedUrl.startsWith(PATHS.INVOICE_MANAGEMENT_CREDIT_NOTES_PAGE))
			navigate(savedUrl);
		else navigate(PATHS.INVOICE_MANAGEMENT_CREDIT_NOTES_PAGE);
	};

	return (
		<Box sx={{ padding: '0 20px 0 20px' }}>
			{creditNote && (
				<>
					<EMManagementIdHeader
						savedUrl={savedUrl}
						emIdProp={creditNote.emId}
						showBackButton={false}
					/>
					<Divider sx={{ my: 1, borderColor: COLORS.PRIMARY }} />
				</>
			)}
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
				<Tooltip title="Back to the Credit Notes list">
					<IconButton onClick={handleBack} color="primary" sx={{ p: 0 }}>
						<KeyboardBackspace />
					</IconButton>
				</Tooltip>
				<Typography variant="h5" color="primary">
					Credit Note Details
				</Typography>
				{!loading && !creditNote && (
					<Typography variant="body2" color="text.secondary">
						Credit note not found
					</Typography>
				)}
			</Box>

			<EasParamsBox<TCreditNote>
				items={(creditNote ?? {}) as TCreditNote}
				boxStructure={creditNoteBoxStructure}
				columnNumber={2}
				editable={false}
				loading={loading}
				fontSize="s"
			/>

			<Box sx={{ mt: 1.5 }}>
				<EasTableWithoutFilter<TCreditNoteInvoiceRow>
					tableStructure={invoicesTableStructure}
					items={invoices}
					itemsIsLoading={loading}
					handleAction={handleAction}
					skeletonRows={[1, 2, 3]}
				>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<Typography variant="body1" color="primary" fontWeight="bold">
							Invoices
						</Typography>
						{loading && <CircularProgress size={20} />}
					</Box>
				</EasTableWithoutFilter>
			</Box>
		</Box>
	);
};
